import React, { useState, useEffect, useCallback } from 'react';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';

const toFa = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const toEng = s => String(s).replace(/[۰-۹]/g,d=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const numFmt = n => { const v=Math.abs(n||0); return v ? v.toLocaleString('fa-IR') : '•'; };
const si = v => { try{return parseInt(toEng(String(v||0)),10)||0;}catch{return 0;} };

/* فراخوانی API + رفرش توکن */
async function API(path, opts={}){
  const doFetch=()=>fetch(`/api/accounting${path}`,{
    headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts});
  let res=await doFetch();
  if(res.status===401){
    const rr=await fetch('/api/auth/token/refresh/',{method:'POST',
      headers:{'Content-Type':'application/json'},
      body:JSON.stringify({refresh:localStorage.getItem('refresh')})});
    if(rr.ok){const d=await rr.json();localStorage.setItem('access',d.access);res=await doFetch();}
  }
  return res.json();
}

/* انتخاب قرارداد آماده برای افزودن به سند */
export default function ContractReadyModal({ year, onClose, onAdded }) {
  const [rawRows,    setRawRows]    = useState([]);
  const [selected,   setSelected]   = useState(null); // تک‌انتخابی
  const [readyNo,    setReadyNo]    = useState('');
  const [docDate,    setDocDate]    = useState('');
  const [serverTotal, setServerTotal] = useState(0);
  const [serverHasMore, setServerHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [toast,      setToast]      = useState('');

  /* عرض ستون‌ها */
  const { widths, TH } = useColumnResize([90,75,140,110,110,110,110,80,200]);

  const {
    visibleRows, search, setSearch,
    filteredCount, shownCount, LoadMoreButton, resetPagination,
  } = useTablePagination({
    allRows: rawRows, pageSize: 200,
    searchFields: ['contract_no','contractor_name','contract_subject'],
      serverTotal,
    serverHasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),4000); };

  useEffect(() => {
    API(`/documents/next-no/?year=${year}`).then(j => { if (j.ok) setDocDate(j.last_date||''); });
  }, [year]);

  /* خواندن فهرست از سرور */
  const load = useCallback(async () => {
    setLoading(true); setError(''); setRawRows([]); setSelected(null); resetPagination();
    const j = await API('/auto-document/contract/ready/');
    setLoading(false);
    if (j.ok) { setRawRows(j.data); setServerTotal(j.total||j.count||0); setServerHasMore(j.has_more||false); setCurrentPage(1); } else setError(j.msg);
  }, [resetPagination]);

  /* ۲۰۰ ردیف بعدی */
  const loadMore = async () => {
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    const j = await API(`/auto-document/contract/ready/?page=${nextPage}`);
    setLoadingMore(false);
    if (j.ok) {
      setRawRows(prev => [...prev, ...j.data]);
      setServerTotal(j.total||0);
      setServerHasMore(j.has_more||false);
      setCurrentPage(nextPage);
    }
  };

  useEffect(() => { load(); }, [load]);

  const sel = selected ? rawRows.find(r => r.contract_id === selected) : null;
  const isNewDoc = si(readyNo) === 0;
  const canRegister = sel && sel.unregistered_doc > 0;
  const canAddCost  = sel && sel.unregistered_contract > 0;

  /* افزودن به سند */
  const addToDoc = async () => {
    if (!sel) { setError('هیچ قراردادی انتخاب نشده'); return; }
    if (canRegister && isNewDoc && !docDate) { setError('تاریخ سند جدید الزامیست'); return; }
    setSaving(true); setError('');
    const j = await API('/auto-document/contract/add/', {
      method:'POST',
      body: JSON.stringify({
        financial_year:year, ready_document_no:si(readyNo), doc_date:docDate,
        contract_row: sel,
      }),
    });
    setSaving(false);
    if (j.ok) { showToast(j.msg); onAdded&&onAdded(); load(); } else setError(j.msg);
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e=>e.stopPropagation()}>
        <div style={s.hdr}>
          <div>
            <div style={{fontSize:'15px',fontWeight:'700',color:'#fff'}}>📋 قرارداد — ثبت حساب انتظامی قرارداد</div>
            <div style={{fontSize:'11px',color:'rgba(255,255,255,0.6)',marginTop:'2px'}}>
              حساب‌های ۸۱۰/۸۲۰ (۸۱۰۰۵/۸۲۰۰۵) — سال مالی {toFa(year)}
            </div>
          </div>
          <button onClick={onClose} style={s.xBtn}>✕</button>
        </div>
        <div style={s.body}>
          {error && <div style={s.err}>⚠️ {error}</div>}

          <div style={{...s.card, background:'#fffdf5', border:'1.5px solid #f0e6c0'}}>
            <div style={{fontSize:'12px',fontWeight:'700',color:'#8d6e00',marginBottom:'8px'}}>🧾 سند مقصد</div>
            <div style={{display:'flex',gap:'14px',flexWrap:'wrap',alignItems:'flex-end'}}>
              <div style={s.fld}>
                <label style={s.lbl}>شماره سند مقصد</label>
                <input value={toFa(readyNo)}
                  onChange={e=>setReadyNo(toEng(e.target.value).replace(/\D/g,'').slice(0,6))}
                  placeholder="خالی = سند جدید" style={{...s.inp,width:'150px'}} />
              </div>
              {isNewDoc && (
                <div style={s.fld}>
                  <label style={s.lbl}>تاریخ سند جدید *</label>
                  <JalaliDatePicker value={docDate} onChange={v=>setDocDate(v)} placeholder="تاریخ"/>
                </div>
              )}
              <div style={{fontSize:'11px',color:'#555',flex:1,minWidth:'200px',lineHeight:'1.7'}}>
                ⚠ تک‌انتخابی — هر بار فقط یک قرارداد انتخاب کنید
              </div>
            </div>
          </div>

          <div style={{display:'flex',gap:'10px',alignItems:'flex-end',flexWrap:'wrap'}}>
            <div style={{...s.fld,flex:1,minWidth:'200px'}}>
              <label style={s.lbl}>جستجو (شماره / پیمانکار / موضوع)</label>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="جستجو..." style={{...s.inp,width:'100%'}} />
            </div>
            <button onClick={load} disabled={loading} style={s.btnS}>
              {loading?'⏳...':'↺ به‌روزرسانی'}
            </button>
            <span style={{fontSize:'11px',color:'#888'}}>{toFa(shownCount)} از {toFa(filteredCount)} ردیف</span>
          </div>

          <div style={{flex:1,overflow:'auto',borderRadius:'8px',boxShadow:'0 1px 6px rgba(0,0,0,0.1)'}}>
            <table style={{width:'100%',borderCollapse:'collapse',direction:'rtl',fontSize:'11px',tableLayout:'fixed'}}>
              <thead>
                <tr>
                  <TH idx={0} right>شماره قرارداد</TH>
                  <TH idx={1}>تاریخ</TH>
                  <TH idx={2} right>پیمانکار</TH>
                  <TH idx={3} red>مبلغ قرارداد</TH>
                  <TH idx={4}>مبلغ سند انتظامی</TH>
                  <TH idx={5} red>سند ثبت نشده</TH>
                  <TH idx={6}>مبلغ ثبت نشده قرارداد</TH>
                  <TH idx={7}>آخرین سند</TH>
                  <TH idx={8} right>موضوع</TH>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r,i)=>{
                  const isSel = selected === r.contract_id;
                  return (
                  <tr key={r.contract_id}
                    onClick={()=>setSelected(isSel ? null : r.contract_id)}
                    style={{background:isSel?'#e8f5e9':i%2===0?'#fff':'#fafafa',cursor:'pointer'}}>
                    <td style={{...s.td,textAlign:'right',fontWeight:'600'}}>{r.contract_no}</td>
                    <td style={{...s.td,fontFamily:'monospace'}}>{r.contract_date}</td>
                    <td style={{...s.td,textAlign:'right'}}>{r.contractor_name}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',color:'#b71c1c'}}>{numFmt(r.contract_cost)}</td>
                    <td style={{...s.td,fontFamily:'monospace'}}>{numFmt(r.debit)}</td>
                    <td style={{...s.td,fontFamily:'monospace',fontWeight:'700',
                      color:r.unregistered_doc>0?'#1b5e20':'#ccc'}}>
                      {r.unregistered_doc>0 ? numFmt(r.unregistered_doc) : '•'}
                    </td>
                    <td style={{...s.td,fontFamily:'monospace',
                      color:r.unregistered_contract>0?'#e65100':'#ccc'}}>
                      {r.unregistered_contract>0 ? numFmt(r.unregistered_contract) : '•'}
                    </td>
                    <td style={{...s.td,fontFamily:'monospace',fontSize:'10px'}}>{r.document_date||'—'}</td>
                    <td style={{...s.td,textAlign:'right',fontSize:'10px'}} title={r.contract_subject}>
                      {r.contract_subject||'—'}
                    </td>
                  </tr>
                );})}
                {!loading && visibleRows.length===0 && (
                  <tr><td colSpan={9} style={{...s.td,padding:'24px',color:'#999'}}>📭 قراردادی برای ثبت سند انتظامی وجود ندارد</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <LoadMoreButton onClick={loadMore} loading={loadingMore} />

          <div style={{display:'flex',alignItems:'center',gap:'12px',flexWrap:'wrap',flexShrink:0}}>
            {sel && (
              <>
                <span style={s.badge('#1a2f50')}>{sel.contract_no}</span>
                {canRegister && <span style={s.badge('#1b5e20')}>سند ثبت نشده: {numFmt(sel.unregistered_doc)}</span>}
                {canAddCost && <span style={s.badge('#e65100')}>اصلاح قرارداد: {numFmt(sel.unregistered_contract)}</span>}
              </>
            )}
            <button onClick={addToDoc} disabled={saving||!sel}
              style={{...s.btnP,marginRight:'auto',opacity:(saving||!sel)?0.4:1}}>
              {saving?'⏳...':(canRegister
                ? (isNewDoc?'💾 ساخت سند انتظامی':'💾 افزودن به سند')
                : '📝 اصلاح تفکیک هزینه قرارداد')}
            </button>
          </div>
        </div>
        {toast && <div style={{position:'absolute',bottom:'14px',right:'14px',background:'#1b5e20',
          color:'#fff',padding:'10px 20px',borderRadius:'8px',fontSize:'13px',fontWeight:'600',zIndex:10}}>{toast}</div>}
      </div>
    </div>
  );
}

/* استایل‌ها */
const s = {
  overlay:   {position:'fixed',inset:0,background:'rgba(0,0,0,0.65)',zIndex:3000,
               display:'flex',alignItems:'center',justifyContent:'center',padding:'12px'},
  modal:     {background:'#f0f2f7',borderRadius:'14px',width:'100%',maxWidth:'1200px',
               maxHeight:'90vh',display:'flex',flexDirection:'column',
               boxShadow:'0 24px 64px rgba(0,0,0,0.5)',overflow:'hidden',position:'relative'},
  hdr:       {display:'flex',justifyContent:'space-between',alignItems:'flex-start',
               padding:'14px 18px',background:'linear-gradient(135deg,#0a1628,#bf360c)',flexShrink:0},
  xBtn:      {background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',
               color:'#fff',width:'28px',height:'28px',borderRadius:'50%',cursor:'pointer',
               fontSize:'12px',display:'flex',alignItems:'center',justifyContent:'center'},
  body:      {flex:1,overflowY:'auto',padding:'14px',display:'flex',flexDirection:'column',gap:'10px'},
  err:       {background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'7px',padding:'8px 12px',color:'#c62828',fontSize:'12px'},
  card:      {background:'#fff',borderRadius:'10px',padding:'14px',boxShadow:'0 1px 4px rgba(0,0,0,0.08)'},
  fld:       {display:'flex',flexDirection:'column',gap:'3px'},
  lbl:       {fontSize:'11px',fontWeight:'600',color:'#555'},
  inp:       {padding:'7px 10px',border:'1.5px solid #e0e0e0',borderRadius:'7px',fontSize:'12px',
               fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box'},
  td:        {padding:'6px 8px',fontSize:'11px',textAlign:'center',borderBottom:'1px solid #f0f0f0',
               whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  btnP:      {padding:'9px 20px',background:'linear-gradient(135deg,#bf360c,#e65100)',color:'#fff',
               border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnS:      {padding:'8px 14px',background:'#e0e0e0',color:'#333',border:'none',
               borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'12px'},
  badge:     bg => ({padding:'5px 12px',background:bg,color:'#fff',borderRadius:'16px',fontSize:'12px',fontWeight:'700'}),
};